import { convertToSnakeCase } from '@stern-ui/utilities';
import clsx from 'clsx';
import {
	AccountClock,
	CheckCircle,
	CloseCircleOutline,
	CheckDecagramOutline,
} from 'mdi-material-ui';
import React from 'react';

import { SkeletonLoader, Typography, PatientStatusObject } from '../components/';

const statusConfig: Record<
	string,
	{
		label?: string;
		backgroundColor?: string;
		textColor?: string;
		border?: string;
		icon?: React.ReactElement;
		className?: string;
	}
> = {
	// Patient Table & Patient Details Panel
	site_consented: {
		label: 'Site consented',
		backgroundColor: 'bg-siteConsented',
	},
	parent_consented: {
		label: 'Parent consented',
		backgroundColor: 'bg-parentConsented',
	},
	vaccine_one_administered: {
		label: 'Day 0 visit completed',
		backgroundColor: 'bg-day0Completed',
	},
	vaccine_two_administered: {
		label: 'Day 28 visit completed',
		backgroundColor: 'bg-day28VisitCompleted',
	},

	// Patient Table
	awaiting_randomisation_table: {
		label: ' ',
		icon: <AccountClock className='w-8 h-8 text-primaryBrand' />,
		className: 'ml-2',
		backgroundColor: 'bg-transparent',
	},
	late_vaccine_group_table: {
		label: 'Late vaccine group',
		border: 'border-vaccineGroupXBorder border py-[3px]',
		icon: <CheckCircle className='w-8 h-8 text-success' />,
		className: 'ml-2',
	},
	early_vaccine_group_table: {
		label: 'Early vaccine group',
		border: 'border-vaccineGroupYBorder border py-[3px]',
		icon: <CheckCircle className='w-8 h-8 text-success' />,
		className: 'ml-2',
	},
	pending: {
		label: 'Pending',
		backgroundColor: 'bg-pending',
	},

	// Patient Details Panel
	early_vaccine_group_panel: {
		label: 'Early vaccine group',
		border: 'border-vaccineGroupYBorder border py-[3px]',
	},
	late_vaccine_group_panel: {
		label: 'Late vaccine group',
		border: 'border-vaccineGroupXBorder border py-[3px]',
	},
	awaiting_randomisation_panel: {
		label: 'Awaiting randomisation ',
		border: 'border-awaitingRandomisation border py-[3px]',
	},
	randomised: {
		label: 'Randomised',
		backgroundColor: 'bg-flusniffRandomised',
	},

	// Data Capture Labels
	day_0_visit: {
		backgroundColor: 'bg-day0Visit',
	},
	day_28_visit: {
		backgroundColor: 'bg-day28Visit',
	},
	consent_confirmation: {
		backgroundColor: 'bg-consentConfirmation',
	},
	consent_re_confirmation: {
		backgroundColor: 'bg-consentReconfirmation',
	},
	eligibility_assessment: {
		backgroundColor: 'bg-eligibilityAssessment',
	},
	weight_kg: {
		backgroundColor: 'bg-weightKg',
	},
	concomitant_medication: {
		border: 'border border-concomitantMedicationBorder',
	},
	imp_administration_at_day_0_visit: {
		backgroundColor: 'bg-impAdministrationDay0',
	},
	imp_administration_at_day_28_visit: {
		backgroundColor: 'bg-impAdministrationDay28',
	},
	parent_guardian_instructions_at_day_0_visit: {
		border: 'border border-parentInstructionsDay0Border',
	},
	parent_guardian_instructions_at_day_28_visit: {
		border: 'border border-parentInstructionsDay28Border',
	},
	survey_review_at_day_28_visit: {
		backgroundColor: 'bg-surveyReviewDay28Visit',
	},
	ae: {
		backgroundColor: 'bg-adverseEvent',
	},
	sae: {
		backgroundColor: 'bg-seriousAdverseEvent',
	},
	patient_randomised: {
		backgroundColor: 'bg-randomised',
	},
	optout: {
		backgroundColor: 'bg-flusniffOptedOut',
	},
	sub_study_yes: {
		label: 'Yes',
		border: 'border border-success',
	},
	sub_study_no: {
		label: 'No',
		border: 'border',
	},
	sub_study_yes_panel: {
		label: 'Interested in sub-study',
		border: 'border border-success',
	},
	sub_study_no_panel: {
		label: 'Not interested in sub-study',
		border: 'border',
	},
	withdrawal: {
		backgroundColor: 'bg-flusniffOptedOut',
	},
};

export const formatChip = (label = 'default', size = 'default') => {
	const formattedLabel = convertToSnakeCase(label);
	const config = statusConfig[formattedLabel] || {
		border: 'border border-primaryBorder',
	};
	const labelVariant = size === 'sm' ? 'bodySmall' : 'body';
	return {
		Element: config ? (
			<div className='flex flex-row items-center text-left'>
				{config.icon}
				{label && (
					<div
						data-label={label}
						className={clsx(
							'rounded-md py-1 ',
							size === 'sm' ? 'px-2' : 'px-3',
							config.backgroundColor || 'bg-white',
							config.border,
							config.className
						)}
					>
						<Typography
							variant={labelVariant}
							className={clsx(config.textColor) || 'text-primaryTextDark'}
						>
							{config.label ? config.label : label}
						</Typography>
					</div>
				)}
			</div>
		) : null,
	};
};

export const formatHciNameAndStatus = (
	name: string,
	isActive: boolean,
	containerClass?: string
) => {
	return (
		<span
			className={`!flex items-center
				before:content-[' '] before:inline-block before:h-[6px] before:w-[6px] before:min-w-[6px] before:rounded-[6px] before:mr-[10px] before:mb-[2px]
				${containerClass || ''}
				${isActive ? 'before:bg-success' : 'before:bg-error'}
			`}
			title={isActive ? 'Active' : 'Inactive'}
		>
			{name}
		</span>
	);
};

export const formatBooleanAsIcon = (
	bool: boolean,
	options?: {
		showLabel?: boolean;
		textClass?: string;
		containerClass?: string;
		customLabel?: { isTrue: string; isFalse: string };
	}
) => {
	const { showLabel = true, textClass, containerClass = '', customLabel } = options || {};
	return bool ? (
		<div className={`flex items-center ${containerClass}`}>
			<CheckDecagramOutline
				fontSize='medium'
				className='text-success mr-1'
				aria-hidden='true'
			/>
			{showLabel && (
				<span className={`align-middle ${textClass}`}>{customLabel?.isTrue || 'Yes'}</span>
			)}
		</div>
	) : (
		<div className={`flex items-center ${containerClass}`}>
			<CloseCircleOutline fontSize='medium' className='text-error mr-1' aria-hidden='true' />
			{showLabel && (
				<span className={`align-middle ${textClass}`}>{customLabel?.isFalse || 'No'}</span>
			)}
		</div>
	);
};

export const formatPatientStatus = (patientStatusObject: PatientStatusObject) => {
	const { cancelled, status, status_label, study_name, type = 'screening' } = patientStatusObject;
	const colorVariants: { [key: string]: string } = {
		// Patient status
		prospect: 'bg-prospect',
		candidate: 'bg-candidate',
		screened: 'bg-screened',
		trained: 'bg-trained',
		readyToEngage: 'bg-readyToEngage',
		completed: 'bg-completed',
		studySiteScheduled: 'bg-studySiteScheduled',
		studySiteScreening: 'bg-studySiteScreening',
		studySiteContacted: 'bg-studySiteContacted',
		statusUnknown: 'bg-statusUnknown',

		// Patient Approver Status
		approved: 'bg-approved',
		engaged: 'bg-engaged',
		excluded: 'bg-excluded',
		consented: 'bg-consented',
		randomised: 'bg-randomised',

		//Patient Screening Status
		screeningRequired: 'bg-screeningRequired',
		screeningPassed: 'bg-screeningPassed',
		screeningFailed: 'bg-screeningFailed',
		requiresReScreening: 'bg-requiresReScreening',
		consentDelegated: 'bg-consentDelegated',

		// Trial Sites Status
		new: 'bg-new',
		deceased: 'bg-trialSiteDeceased',
		optedOut: 'bg-trialSiteOptedOut',
		trialSiteContacted: 'bg-trialSiteContacted',
		trialSiteScheduled: 'bg-trialSiteScheduled',
		trialSiteScreening: 'bg-trialSiteScreening',

		// Letter Status
		scheduled: 'bg-scheduled',
		sentToPrint: 'bg-sentToPrint',
		printing: 'bg-printing',
		inThePost: 'bg-inThePost',
		inTransit: 'bg-inTransit',
		atDeliveryOffice: 'bg-atDeliveryOffice',
		delivered: 'bg-delivered',
		returned: 'bg-returned',
		letterCancelled: 'bg-letterCancelled',
		failed: 'bg-failed',

		// Mail Status
		producing: 'bg-producing',
		dispatched: 'bg-dispatched',
		mailCancelled: 'bg-mailCancelled',

		// Active Status
		active: 'bg-active',
		inactive: 'bg-inactive',
		hold: 'bg-hold',

		// Survey screening result
		pass: 'bg-surveyPass',
		fail: 'bg-surveyFailed',
		['re-screening']: 'bg-surveyRescreening',
		['opt-out']: 'bg-surveyOptedOut',
	};

	const getStatusTitle = (title: string, color: string) => {
		return (
			<div className='flex flex-row items-center text-left'>
				<span className={`flex flex-row ${colorVariants[color]} rounded py-1 px-3`}>
					<Typography variant='body_md_regular' className='text-primaryTextDark'>
						{`${study_name}: `}
					</Typography>
					<Typography variant='body_md_semibold' className='text-primaryTextDark'>
						<>&nbsp;{title}</>
					</Typography>
				</span>
			</div>
		);
	};
	if (cancelled === 2000) {
		return {
			statusValue: 'Opted Out',
			Element: getStatusTitle('Opted out', 'optedOut'),
		};
	}

	if (cancelled === 6900) {
		return {
			statusValue: 'Excluded',
			Element: getStatusTitle('Excluded', 'excluded'),
		};
	}

	if (cancelled === 8000) {
		return {
			statusValue: 'Deceased',
			Element: getStatusTitle('Deceased', 'deceased'),
		};
	}

	if (status === 70 && cancelled === 0) {
		return {
			statusValue: 'Prospect',
			Element: getStatusTitle('Prospect', 'prospect'),
		};
	}

	if (status === 100 && cancelled === 0) {
		return {
			statusValue: 'Candidate',
			Element: getStatusTitle('Candidate', 'candidate'),
		};
	}

	if (status === 500 && cancelled === 0) {
		return {
			statusValue: 'Approved',
			Element: getStatusTitle('Approved', 'approved'),
		};
	}

	if (status === 510 && cancelled === 0) {
		return {
			statusValue: 'Ready to Engage',
			Element: getStatusTitle('Ready to Engage', 'readyToEngage'),
		};
	}
	if (status === 520 && cancelled === 0) {
		return {
			statusValue: 'Engaged',
			Element: getStatusTitle('Engaged', 'engaged'),
		};
	}

	if (status === 530 && cancelled === 0) {
		return {
			statusValue: 'Consent Delegated',
			Element: getStatusTitle(
				'Consent Delegated',
				type === 'screening' ? 'consentDelegated' : 'consentDelegated'
			),
		};
	}

	if (status === 540 && cancelled === 0) {
		return {
			statusValue: 'Consent Pending',
			Element: getStatusTitle('Consent Pending', 'consentPending'),
		};
	}

	if (status === 550 && cancelled === 0) {
		if (type === 'screening') {
			return {
				statusValue: 'Screening required',
				Element: getStatusTitle('Screening required', 'screeningRequired'),
			};
		}
		return {
			statusValue: 'Consented',
			Element: getStatusTitle('Consented', 'consented'),
		};
	}

	if ((status === 550 || status === 580) && cancelled == 6800) {
		return {
			statusValue: 'Screening failed',
			Element: getStatusTitle('Screening failed', 'screeningFailed'),
		};
	}

	if (status === 580) {
		return {
			statusValue: 'Re-screening required',
			Element: getStatusTitle('Re-screening required', 'requiresReScreening'),
		};
	}

	if (status === 600) {
		if (type === 'screening') {
			return {
				statusValue: 'Screening passed',
				Element: getStatusTitle('Screening passed', 'screeningPassed'),
			};
		}
		if (cancelled === 0) {
			return {
				statusValue: type === 'trial' ? 'New' : 'Screened',
				Element:
					type == 'trial'
						? getStatusTitle('New', 'new')
						: getStatusTitle('Screened', 'screened'),
			};
		}
	}

	if (status === 602 && cancelled === 0) {
		return {
			statusValue: 'Contacted',
			Element:
				type == 'trial'
					? getStatusTitle('Contacted', 'trialSiteContacted')
					: getStatusTitle('Study Site Contacted', 'studySiteContacted'),
		};
	}

	if (status === 604 && cancelled === 0) {
		return {
			statusValue: 'Scheduled',
			Element:
				type == 'trial'
					? getStatusTitle('Scheduled', 'trialSiteScheduled')
					: getStatusTitle('Study Site Scheduled', 'studySiteScheduled'),
		};
	}

	if (status === 606 && cancelled === 0) {
		return {
			statusValue: 'Screening',
			Element:
				type == 'trial'
					? getStatusTitle('Screening', 'trialSiteScreening')
					: getStatusTitle('Study Site Screening', 'studySiteScreening'),
		};
	}

	if (status === 610 && cancelled === 0) {
		return {
			statusValue: 'Randomised',
			Element: getStatusTitle(
				'Randomised',
				type === 'screening' ? 'randomised' : 'randomised'
			),
		};
	}

	if (status === 800 && cancelled === 0) {
		return {
			statusValue: 'Trained',
			Element: getStatusTitle('Trained', 'trained'),
		};
	}

	if (status === 1000 && cancelled === 0) {
		return {
			statusValue: 'Completed',
			Element: getStatusTitle('Completed', 'completed'),
		};
	}

	return {
		statusValue: status_label,
		Element: getStatusTitle(
			type === 'screening' ? 'Status unknown' : status_label,
			'statusUnknown'
		),
	};
};

export const renderSkeletonTable = (rows: number, columns: number) => {
	return Array.from({ length: rows }).map((_, rowIndex) => (
		<div
			key={rowIndex}
			className={`flex flex-row w-full justify-between ${rowIndex === 0 ? 'mb-8' : 'mb-4'}`}
		>
			{Array.from({ length: columns }).map((_, colIndex) => (
				<SkeletonLoader
					key={colIndex}
					className={`${rowIndex === 0 ? 'h-12' : 'h-8'} w-full rounded-md mb-2${colIndex < columns - 1 ? ' mr-4' : ''}`}
				/>
			))}
		</div>
	));
};

export const statusIsComplete = (status: string) => {
	return ['Excluded', 'Randomised'].includes(status);
};

export const screeningIsComplete = (status: string) => {
	return [
		'Screened',
		'Screening failed',
		'Screening passed',
		'Consent Delegated',
		'Contacted',
		'Scheduled',
		'Screening',
	].includes(status);
};

export const objBoolToObjString = (obj: { [key: string]: boolean }) => {
	// convert all boolean values in the object to string values
	return Object.entries(obj).reduce(
		(newObj: { [key: string]: string }, [key, value]) => ({
			...newObj,
			[key]: value.toString(),
		}),
		{}
	);
};

export const strObjToBoolObj = (obj: { [key: string]: string }) => {
	// convert all string values in the object to boolean values
	return Object.entries(obj).reduce(
		(newObj: { [key: string]: boolean }, [key, value]) => ({
			...newObj,
			[key]: value === 'true',
		}),
		{}
	);
};

export const iconClassNames =
	'bg-primaryBrand text-primaryBrandBackground flex items-center justify-center rounded-full w-[32px] h-[32px] text-heading-5 font-semibold';
